import { useId, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BellRing, Upload, Users } from 'lucide-react';
import { adminQueries, getErrorMessage, keys } from '@blimo/api-client';
import { useApi } from '@blimo/api-client/react';
import { formatNumber, hasPermission } from '@blimo/shared';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  DocumentList,
  EmptyState,
  ErrorState,
  FileDropzone,
  Label,
  Skeleton,
  toast,
} from '@blimo/ui';
import { SendDialog } from '../../components/send-dialog.jsx';
import { recipientsText, useInvalidateCheck } from './utils.js';

/** Die sieben Ordner der Prüfung mit Dokumenten, Upload und Benachrichtigung je Dokument. */
export function FoldersTab({ check, folders, me }) {
  const [notify, setNotify] = useState(/** @type {null|{ kind: 'auditors'|'owners', doc: any }} */ (null));
  if (!hasPermission(me, 'documents.view')) {
    return <EmptyState title="Keine Berechtigung für Dokumente" description="Zum Anzeigen der Ordnerinhalte ist das Recht „Dokumente ansehen“ nötig." />;
  }
  if (folders.length === 0) return <EmptyState title="Keine Ordner" description="Für diese Prüfung sind keine Ordner angelegt." />;
  return (
    <div className="flex flex-col gap-3">
      {folders.map((folder) => (
        <FolderCard key={folder.id} check={check} folder={folder} me={me} onNotify={(kind, doc) => setNotify({ kind, doc })} />
      ))}
      <NotifyDialog check={check} target={notify} onClose={() => setNotify(null)} />
    </div>
  );
}

function FolderCard({ check, folder, me, onNotify }) {
  const api = useApi();
  const query = useQuery(adminQueries.folderDocuments(api, folder.id));
  const canRelease = hasPermission(me, 'document-checks.release');
  const canUpload = hasPermission(me, 'documents.upload');
  const headingId = useId();

  return (
    <Card role="region" aria-labelledby={headingId}>
      <CardHeader className="flex-row items-center justify-between gap-2">
        <div className="flex flex-col gap-0.5">
          <CardTitle as="h3" id={headingId}>
            {folder.name}
          </CardTitle>
          <CardDescription>{formatNumber(query.data?.length ?? folder.documents_count ?? 0)} Dokumente</CardDescription>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {query.error ? (
          <ErrorState compact error={query.error} onRetry={() => query.refetch()} />
        ) : query.isPending ? (
          <Skeleton className="h-16 w-full" />
        ) : (
          <DocumentList
            label={`Dokumente ${folder.name}`}
            documents={query.data}
            hrefFor={(doc) => api.admin.documents.downloadUrl(doc.id)}
            showPublished
            emptyText="Noch keine Dokumente in diesem Ordner."
            actions={
              canRelease
                ? (doc) => (
                    <span className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => onNotify('auditors', doc)} disabled={!check.released_to_auditors}>
                        <BellRing aria-hidden="true" /> Kassenprüfer informieren
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => onNotify('owners', doc)}>
                        <Users aria-hidden="true" /> Eigentümer informieren
                      </Button>
                    </span>
                  )
                : undefined
            }
          />
        )}
        {canUpload && <FolderUpload folder={folder} canPublish={hasPermission(me, 'documents.publish')} />}
      </CardContent>
    </Card>
  );
}

/** Upload (one request per file – the documents endpoint takes a single file). */
function FolderUpload({ folder, canPublish }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const invalidate = useInvalidateCheck();
  const [files, setFiles] = useState(/** @type {File[]} */ ([]));
  const [publish, setPublish] = useState(canPublish);
  const [pending, setPending] = useState(false);
  const publishId = useId();

  async function upload() {
    setPending(true);
    let done = 0;
    try {
      for (const file of files) {
        await api.admin.documentFolders.upload(folder.id, { file, publish: canPublish ? publish : undefined });
        done += 1;
      }
      toast.success(`${formatNumber(done)} ${done === 1 ? 'Dokument' : 'Dokumente'} hochgeladen`);
      setFiles([]);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Der Upload ist fehlgeschlagen.'));
      setFiles(files.slice(done));
    } finally {
      setPending(false);
      queryClient.invalidateQueries({ queryKey: keys.admin.resource('document-folders') });
      invalidate();
    }
  }

  return (
    <div className="flex flex-col gap-2 border-t pt-3">
      <FileDropzone label={`Dateien für ${folder.name}`} files={files} onFilesChange={setFiles} disabled={pending} hint="Max. 20 MB je Datei." />
      {files.length > 0 && (
        <div className="flex flex-wrap items-center gap-3">
          {canPublish && (
            <span className="flex items-center gap-2">
              <Checkbox id={publishId} checked={publish} onChange={(e) => setPublish(e.target.checked)} />
              <Label htmlFor={publishId} className="font-normal">
                Sofort veröffentlichen
              </Label>
            </span>
          )}
          <Button size="sm" onClick={upload} loading={pending}>
            <Upload aria-hidden="true" /> Hochladen
          </Button>
        </div>
      )}
    </div>
  );
}

/** Kassenprüfer bzw. Eigentümer über ein Dokument informieren (mit Bestätigung). */
function NotifyDialog({ check, target, onClose }) {
  const api = useApi();
  const invalidate = useInvalidateCheck();
  const owners = target?.kind === 'owners';
  const name = target?.doc?.title || target?.doc?.original_name || '';
  return (
    <SendDialog
      open={Boolean(target)}
      onOpenChange={(open) => !open && onClose()}
      title={owners ? 'Eigentümer informieren?' : 'Kassenprüfer informieren?'}
      description={
        owners ? `Alle aktuellen Eigentümer erhalten eine E-Mail zu „${name}“ (mit Anhang).` : `Die Kassenprüfer erhalten eine E-Mail zum Dokument „${name}“.`
      }
      send={() => (owners ? api.admin.documentChecks.notifyOwners(check.id, target.doc.id) : api.admin.documentChecks.notifyAuditors(check.id, target.doc.id))}
      onSent={(result) => {
        invalidate();
        toast.success(recipientsText(result));
      }}
    >
      {!owners && !check.released_to_auditors && <Badge variant="warning">Die Prüfung ist noch nicht freigegeben.</Badge>}
    </SendDialog>
  );
}
